import React, { createContext, useContext, ReactNode } from 'react';
import { useGetAuth0MeQuery } from '../store/api/userApi';
import { useAuthReadiness } from './AuthReadinessContext';

interface PermissionsContextType {
  allowedActions: string[];
  hasPermission: (action: string) => boolean;
  hasViewPermission: (module: string) => boolean;
  hasActionPermission: (module: string, action: 'add' | 'edit' | 'delete' | 'execute' | 'manage') => boolean;
  isLoading: boolean;
}

const PermissionsContext = createContext<PermissionsContextType>({
  allowedActions: [],
  hasPermission: () => false,
  hasViewPermission: () => false,
  hasActionPermission: () => false,
  isLoading: true,
});

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const { isAuthReady, isLoading: authLoading } = useAuthReadiness();
  
  // Only make the API call when authentication is ready
  const { data: auth0MeData, isLoading: apiLoading } = useGetAuth0MeQuery(undefined, {
    skip: !isAuthReady
  });
  
  const allowedActions = auth0MeData?.user?.allowed_ui_actions || [];
  const isLoading = authLoading || apiLoading;

  const hasPermission = (action: string): boolean => {
    return allowedActions.includes(action);
  };

  const hasViewPermission = (module: string): boolean => {
    return hasPermission(`view_${module}_scr`);
  };

  const hasActionPermission = (module: string, action: 'add' | 'edit' | 'delete' | 'execute' | 'manage'): boolean => {
    return hasPermission(`${action}_${module}_scr`);
  };

  const value: PermissionsContextType = {
    allowedActions,
    hasPermission,
    hasViewPermission,
    hasActionPermission,
    isLoading,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export default PermissionsContext;